import React, { useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  CircularProgress,
  Popper,
  ClickAwayListener
} from '@mui/material';
import {
  LocationOn,
  MyLocation,
  Search,
  Place
} from '@mui/icons-material';

// Fix for default markers
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface MapSelectorProps {
  initialLat?: number;
  initialLng?: number;
  onLocationSelect: (lat: number, lng: number) => void;
}

interface ClickableMapProps {
  onMapClick: (lat: number, lng: number) => void;
  selectedPosition: [number, number] | null;
}

interface SearchResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  address?: {
    name?: string;
    amenity?: string;
    road?: string;
    neighbourhood?: string;
    suburb?: string;
    city?: string;
    state?: string;
    country?: string;
  };
}

// Component to recenter map when search result is selected
const MapRecenter: React.FC<{ center: [number, number] | null }> = ({ center }) => {
  const map = useMap();
  React.useEffect(() => {
    if (center) {
      map.setView(center, 16);
    }
  }, [center, map]);
  return null;
};

const ClickableMap: React.FC<ClickableMapProps> = ({ onMapClick, selectedPosition }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onMapClick(lat, lng);
    },
  });

  return selectedPosition ? <Marker position={selectedPosition} /> : null;
};

const MapSelector: React.FC<MapSelectorProps> = ({
  initialLat = 41.0082,
  initialLng = 28.9784,
  onLocationSelect
}) => {
  const [selectedPosition, setSelectedPosition] = useState<[number, number] | null>(
    initialLat && initialLng ? [initialLat, initialLng] : null
  );
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchInputRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Turkey center coordinates (Istanbul as default)
  const defaultCenter: [number, number] = [initialLat, initialLng];
  const defaultZoom = 10;

  // Nominatim search function
  const searchPlaces = async (query: string) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=tr&limit=8&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'tr'
          }
        }
      );
      const data: SearchResult[] = await response.json();
      setSearchResults(data);
      setShowResults(true);
    } catch (error) {
      console.error('Arama hatası:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchPlaces(value);
    }, 300);
  };

  // Handle search result selection
  const handleSelectResult = (result: SearchResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    const newPosition: [number, number] = [lat, lng];

    setSelectedPosition(newPosition);
    setMapCenter(newPosition);
    onLocationSelect(lat, lng);
    setShowResults(false);
    setSearchQuery(result.display_name);
  };

  const handleMapClick = (lat: number, lng: number) => {
    const newPosition: [number, number] = [lat, lng];
    setSelectedPosition(newPosition);
    onLocationSelect(lat, lng);
  };

  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const newPosition: [number, number] = [latitude, longitude];
          setSelectedPosition(newPosition);
          onLocationSelect(latitude, longitude);
        },
        (error) => {
          console.error('Konum alınamadı:', error);
          alert('Konum erişimi reddedildi veya mevcut değil.');
        }
      );
    } else {
      alert('Tarayıcınız konum servislerini desteklemiyor.');
    }
  };

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      {/* Info Header */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Harita Üzerinden Konum Seç
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<MyLocation />}
            onClick={handleCurrentLocation}
            sx={{ borderRadius: 2 }}
          >
            Mevcut Konum
          </Button>
        </Box>

        {/* Search Box */}
        <Box sx={{ position: 'relative', mb: 2 }} ref={searchInputRef}>
          <ClickAwayListener onClickAway={() => setShowResults(false)}>
            <Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Kafe veya yer ara... (örn: Starbucks Kadıköy)"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => searchResults.length > 0 && setShowResults(true)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: isSearching ? (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : null
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
              <Popper
                open={showResults && searchResults.length > 0}
                anchorEl={searchInputRef.current}
                placement="bottom-start"
                style={{ zIndex: 9999, width: searchInputRef.current?.offsetWidth }}
              >
                <Paper
                  elevation={4}
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    mt: 0.5,
                    borderRadius: 2
                  }}
                >
                  <List dense>
                    {searchResults.map((result) => (
                      <ListItem key={result.place_id} disablePadding>
                        <ListItemButton onClick={() => handleSelectResult(result)}>
                          <Place sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                          <ListItemText
                            primary={result.display_name.split(',')[0]}
                            secondary={result.display_name.split(',').slice(1, 4).join(',')}
                            primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Popper>
            </Box>
          </ClickAwayListener>
        </Box>

        <Alert severity="info" sx={{ mb: 1 }}>
          Yer adı yazarak arayın veya harita üzerine tıklayarak kafe konumunu seçin.
        </Alert>

        {selectedPosition && (
          <Alert severity="success">
            <strong>Seçilen Konum:</strong>
            <br />
            Enlem: {selectedPosition[0].toFixed(6)}
            <br />
            Boylam: {selectedPosition[1].toFixed(6)}
          </Alert>
        )}
      </Paper>

      {/* Map Container */}
      <Box sx={{ height: 400, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          <MapRecenter center={mapCenter} />

          <ClickableMap
            onMapClick={handleMapClick}
            selectedPosition={selectedPosition}
          />
        </MapContainer>
      </Box>
    </Box>
  );
};

export default MapSelector;
